import Button from "components/form/Button";
import InputField from "components/form/InputField";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  decode,
  downloadPdfFromApi,
  encode,
  formatCurrency,
  validateEmail,
} from "utils/HelperFunctions";
import Drawer from "components/ui-component/SideDrawer";
import { SuccessTick } from "icons/auth/SuccessTick";
import {
  paymentReceiptDownloadPdf,
  receiptIdGet,
} from "store/slices/paymentsSlice";
import { useLeadData, usePortalVanityUrl } from "store/slices/dashboardSlice";
import { useAppDispatch } from "store/hooks";
import { formatPaymentListDate } from "utils/DateFormat";
import { useUserData } from "store/slices/authSlice";
import { useMediaQuery } from "react-responsive";
import SubscriptionsPreview from "components/subscriptions/SubscriptionsPreview";
import MessageToast from "components/ui-component/MessageToast";
import moment from "moment";
import { ToastState } from "types/common";
import { useTestAttrsFactory } from "utils/testid";
import { canOpenPaymentPlanPreview } from "./paymentScheduledSuccessUtils";

const PaymentScheduledSuccess = ({
  email,
  amount,
  scheduleId,
  sendEmail,
  date,
  orderId,
  showSuccessToast = false,
}: any) => {
  const [emailInput, setEmailInput] = useState("");
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const [id, setId] = useState(scheduleId);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState("");
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const accountLeadData = useLeadData();
  const vanityUrl = usePortalVanityUrl();
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [receiptId, setReceiptId] = useState<any>(null);
  const [isRecieptDownloaded, setIsRecieptDownloaded] =
    useState<boolean>(false);
  const [msg, setMsg] = useState<ToastState>({
    variant: "",
    content: null,
  });
  const testAttrs = useTestAttrsFactory();
  const canOpenPreview = canOpenPaymentPlanPreview(scheduleId);

  console.log("id ======", id);

  const handleSendEmail = async () => {
    setIsEmailLoading(true);
    if (!validateEmail(emailInput)) {
      setIsErr(true);
      setErrMsg("Invalid Email");
      setIsEmailLoading(false);
      return;
    }
    try {
      let emails = [emailInput];
      let value = {
        invoice_id: orderId || scheduleId,
        emails: emails,
        isRealTime: !date || moment().isSame(moment(date), "days"),
        isFuture: true,
        schedule_id: scheduleId,
        shouldSendAll: false,
      };
      await sendEmail(value);
      setEmailInput("");
      if (showSuccessToast) {
        setIsEmailSent(true);
        setMsg({
          ...msg,
          variant: "success",
          content: "Payment plan sent successfully",
        });
      }
    } catch (error) {
      console.error("Failed to send email:", error);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const onPaymentDownload = async (id: any, receipt_id?: any) => {
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    let payload = {
      invoice_id: id,
      lead_id: accountLeadData?.lead_id,
      url: `${vanityUrl}view-payment/${pid}`,
      cluster_id: clusterId,
      isFromPortal: true,
    };

    await downloadPdfFromApi(
      () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
      `Receipt - ${receipt_id || id}.pdf`,
      (loading) => setIsDownloadLoading(loading ? scheduleId : "")
    );

    if (showSuccessToast) {
      setIsRecieptDownloaded(true);
      setMsg({
        ...msg,
        variant: "success",
        content: "Payment plan downloaded successfully",
      });
    }
  };

  const handleGetReceiptID = async (params: any) => {
    try {
      const response: any = await dispatch(receiptIdGet(params)).unwrap();
      if (response?.body?.success) {
        setReceiptId(response.body.data?.id);
        return response.body.data?.id;
      }
      return params?.orders_id;
    } catch (error) {
      console.error("Failed to get receipt ID:", error);
    }
  };

  const handleDownload = async () => {
    let receipt_id = receiptId;
    if (!receipt_id) {
      receipt_id = await handleGetReceiptID({
        orders_id: orderId || id,
      });
    }

    await onPaymentDownload(orderId || id, receipt_id);
  };

  return (
    <div
      className="w-full h-full max-w-[640px]"
      {...testAttrs("scheduled-payment-success", "Scheduled payment success")}
    >
      <div className="h-full flex flex-col justify-center items-center gap-24">
        <div className="w-full flex flex-col justify-center items-center gap-10">
          <SuccessTick aria-hidden="true" />
          <div className="text-center text-txtPrimary text-2xl leading-8 font-medium">
            Payment plan created successfully
          </div>
          <div className="w-full flex flex-col justify-center items-center">
            <div className="w-full text-center bg-bgThird py-5 px-2 text-xl text-txtParagraph mb-4 font-normal">
              {!date || moment().isSame(moment(date), "days") ? (
                <>
                  Your first scheduled payment of{" "}
                  <span className="font-bold">
                    {formatCurrency(amount) || "$0.00"}
                  </span>{" "}
                  has been received.
                </>
              ) : (
                <>
                  Your first payment of{" "}
                  <span className="font-bold">
                    {formatCurrency(amount) || "$0.00"}
                  </span>{" "}
                  is scheduled for{" "}
                  <span className="font-bold">
                    {formatPaymentListDate(
                      date,
                      userData?.data?.accountData?.timezone,
                      "long"
                    )}
                    .
                  </span>{" "}
                </>
              )}
            </div>
            {canOpenPreview && (
              <div className="text-txtParagraph text-center text-base">
                <span
                  className="text-btnPrimaryActive hover:!text-btnPrimaryHover underline cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setId(scheduleId);
                    setIsPreview(true);
                  }}
                  {...testAttrs(
                    "view-payment-plan-details",
                    "View payment plan details"
                  )}
                >
                  Click here
                </span>{" "}
                to view your full payment plan details.
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="flex-col flex justify-center items-center gap-[30px]">
            <div className="text-txtParagraph lg:text-base text-sm whitespace-normal break-words text-center">
              A copy of this payment plan will automatically be sent to{" "}
              <span className="font-bold">{email}</span>. To send a copy to an
              additional email, enter the address below.
            </div>
            <div className="px-[10px] w-full">
              <InputField
                type="email"
                name="email"
                label=""
                placeholder="Enter your email address"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setIsErr(false);
                  setErrMsg(undefined);
                }}
                className="!py-3"
                error={errMsg}
                isErr={isErr}
                {...testAttrs(
                  "payment-plan-email",
                  "Additional email for payment plan"
                )}
              />
            </div>
            <div className="m-[10px] w-full flex justify-center items-center gap-5">
              <Button
                text="Send Email"
                className="w-full"
                isLoading={isEmailLoading}
                isDisabled={emailInput.length === 0 && !isEmailSent}
                isSuccess={isEmailSent}
                onClick={() => {
                  handleSendEmail();
                }}
                {...testAttrs("send-payment-plan-email", "Send payment plan")}
              />
              <Button
                text={
                  !date || moment().isSame(moment(date), "days")
                    ? "Download"
                    : "Manage Plan"
                }
                className="w-full"
                isLoading={isDownloadLoading !== ""}
                onClick={() => {
                  if (!date || moment().isSame(moment(date), "days")) {
                    handleDownload();
                  } else {
                    navigate(`/${cluster_id}/subscriptions-plans`);
                  }
                }}
                isSuccess={isRecieptDownloaded}
                {...testAttrs(
                  !date || moment().isSame(moment(date), "days")
                    ? "download-payment-plan"
                    : "manage-payment-plan",
                  !date || moment().isSame(moment(date), "days")
                    ? "Download payment plan"
                    : "Manage payment plan"
                )}
              />
            </div>
          </div>
          <div className="w-full mt-6  lg:text-base text-sm text-center text-btnPrimaryActive hover:!text-btnPrimaryHover ">
            <div
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/${cluster_id}/dashboard`)}
              {...testAttrs("back-to-dashboard", "Back to dashboard")}
            >
              Back to dashboard
            </div>
          </div>
        </div>
      </div>
      <Drawer
        direction="right"
        open={isPreview && canOpenPreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setIsPreview(false);
          setId("");
        }}
        overlayColor="#000"
        lockBackgroundScroll={true}
        enableOverlay={true}
      >
        <div {...testAttrs("payment-plan-preview", "Payment plan preview")}>
          <SubscriptionsPreview
            id={id}
            setId={setId}
            show={isPreview}
            setShow={setIsPreview}
            setHandleDrawerSize={() => {}}
            isApiCall={true}
            isScheduled={false}
          />
        </div>
      </Drawer>
      <div
        className="relative max-w-[560px] !bottom-0 z-[9999999]"
        {...testAttrs("payment-plan-toast", "Payment plan notification")}
      >
        <MessageToast
          variant={msg?.variant}
          content={msg?.content}
          isProgressBar
          width="w-full !-right-0 "
          className="!absolute "
          onCloseClick={() => {
            setMsg({
              ...msg,
              variant: "",
              content: null,
            });
            setIsEmailSent(false);
            setIsRecieptDownloaded(false);
          }}
        />
      </div>
    </div>
  );
};

export default PaymentScheduledSuccess;
